import { Injectable, inject, signal } from '@angular/core';
import type { Observable } from 'rxjs';
import { firstValueFrom, fromEvent, map, takeUntil } from 'rxjs';
import { type WebSocketSubject, webSocket } from 'rxjs/webSocket';

import { AuthenticationService } from '@app/auth/authentication.service';
import {
    transferUploads,
    type UploadProgress,
    type UploadRequest,
} from '@app/settings/platform-settings/platform-settings-advanced/uploads-transfer';
import { SYSTEM_PATH } from '@app/shared/api/api-route.config';
import type { ListResponse } from '@app/shared/api/crud.service';
import { RestService } from '@app/shared/api/rest.service';

export interface DBUpgradeLogEvent {
    type: 'auth' | 'log' | 'completed';
    token?: string; // only for type=auth
    key?: string; // only for type=auth, when the server asks for the admin key
    backup?: boolean; // only for type=auth, a database backup is made first unless this is false
    message?: string; // Only if type=log
    success?: boolean; // Only if type=completed
    error?: string; // Only if type=completed
}

export interface BackupEvent {
    type: 'auth' | 'progress' | 'completed' | 'error';
    token?: string; // only for type=auth
    data?: { n: number; total: number | null }; // only if type=progress
    filename?: string; // only if type=completed
    message?: string; // only if type=error
}

export interface BackupListItem {
    filename: string;
    sizeBytes: number;
}

@Injectable({
    providedIn: 'root',
})
export class DatabaseMigrationService {
    private readonly restService = inject(RestService);
    readonly visible = signal(false);
    private readonly authService = inject(AuthenticationService);

    dbUpgrade(key?: string, backup = true): WebSocketSubject<DBUpgradeLogEvent> {
        const wsSubject = webSocket<DBUpgradeLogEvent>(
            `${this.restService.baseUrl()}${SYSTEM_PATH}upgrade`,
        );
        // rxjs webSocket() doesn't allow different types for sending an receiving messages, so use any
        wsSubject.next({ type: 'auth', token: this.authService.getToken(), key, backup });
        return wsSubject;
    }

    createBackup(): WebSocketSubject<BackupEvent> {
        const wsSubject = webSocket<BackupEvent>(
            `${this.restService.baseUrl()}${SYSTEM_PATH}backup/export`,
        );
        wsSubject.next({ type: 'auth', token: this.authService.getToken() });
        return wsSubject;
    }

    listBackups(): Observable<BackupListItem[]> {
        return this.restService
            .get<ListResponse<BackupListItem>>(`${SYSTEM_PATH}backup/db`)
            .pipe(map(response => response.result));
    }

    /**
     * Provides a URL that the browser can download directly using the short-lived download token.
     * Avoid using downloadAttachment when possible.
     */
    getDownloadBackupUrl(filename: string): string {
        const query = new URLSearchParams({ filename, token: this.authService.getDownloadToken() });
        const url = `${this.restService.baseUrl()}${SYSTEM_PATH}backup/db/download`;
        return `${url}?${query.toString()}`;
    }

    downloadFileStorage(
        destination: FileSystemDirectoryHandle,
        onProgress: (progress: UploadProgress) => void,
        signal: AbortSignal,
    ): Promise<void> {
        return transferUploads(
            destination,
            this.restService.baseUrl(),
            this.requestFileStorage,
            onProgress,
            signal,
        );
    }

    private readonly requestFileStorage: UploadRequest = async (
        endpoint,
        params,
        signal,
        method = 'GET',
    ) => {
        signal.throwIfAborted();
        const route = endpoint === 'manifest' ? 'manifest' : 'download';
        const query = endpoint === 'batch' ? '' : `?${new URLSearchParams(params)}`;
        const url = `${this.restService.baseUrl()}${SYSTEM_PATH}backup/filestorage/${route}${query}`;
        const fetchResponse = () =>
            fetch(url, {
                method,
                signal,
                cache: 'no-store',
                headers: {
                    Authorization: `JWT ${this.authService.getToken()}`,
                    ...(endpoint === 'batch' ? { 'Content-Type': 'application/json' } : {}),
                },
                body: endpoint === 'batch' ? params.files : undefined,
            });
        let response = await fetchResponse();
        if (response.status === 401) {
            // Use the existing interceptor to refresh authentication for long-running transfers.
            await firstValueFrom(
                this.restService
                    .get('/users/whoami', false)
                    .pipe(takeUntil(fromEvent(signal, 'abort'))),
            );
            response = await fetchResponse();
        }
        if (!response.ok) {
            await response.body?.cancel();
            const reason =
                response.status === 409
                    ? 'The upload changed during transfer.'
                    : response.status === 404
                      ? 'The upload no longer exists.'
                      : response.status === 401 || response.status === 403
                        ? 'Administrator access is required. Sign in again.'
                        : `The server returned HTTP ${response.status}.`;
            throw new Error(`${params.path || 'File scan'}: ${reason} Retry the transfer.`);
        }
        return response;
    };
}
